import app from 'firebase/compat/app';
import 'firebase/compat/auth';
import 'firebase/compat/firestore';
import 'firebase/compat/functions';
import 'firebase/compat/storage';
import 'firebase/compat/analytics';
import * as Amplitude from 'amplitude-js';

import { v4 as uuidv4 } from 'uuid';

const config = {
  apiKey: "AIzaSyD0u-Iu0F1x3_ktwll2GOevp1_ssv84QGE",
  authDomain: "athletes-launch.firebaseapp.com",
  databaseURL: "https://athletes-launch.firebaseio.com",
  projectId: "athletes-launch",
  storageBucket: "athletes-launch.appspot.com",
  messagingSenderId: "527335828485",
  appId: "1:527335828485:web:533186c71be4d66599406c",
  measurementId: "G-4J827MQXEL"
};

class Firebase {
  constructor() {
    app.initializeApp(config);

    this.auth = app.auth();
    this.db = app.firestore();
    this.firestore = this.db;   // alias — keeps existing usages working
    this.functions = app.functions();
    this.storage = app.storage();
    this.analytics = app.analytics();
    this.amplitudeClient = Amplitude.getInstance();
    this.amplitudeClient.init('90b4b8e3ab198e6d349ef4e59318cc6e');
  }

  findCompany(companyName) {
    return this.firestore
      .collection('organizations')
      .where('name', '==', companyName)
      .get();
  }

  login(username, password) {
    return this.auth.signInWithEmailAndPassword(username, password)
      .then(() => {
        return this.firestore.collection("users").doc(this.auth.currentUser.uid).get();
      });
  }

  logout() {
    this.trackAnalyticsEvent('logout');
    this.analytics.setUserId('')
    this.auth.signOut();
  }

  // Alias used by EmailVerificationPending and other components
  signOut() {
    this.logout();
  }

  resendVerificationEmail() {
    const user = this.auth.currentUser;
    if (!user) return Promise.reject(new Error('No user is signed in.'));
    return user.sendEmailVerification();
  }

  useProfile(callback) {
    return this.firestore.collection('users').doc(this.auth.currentUser.uid).onSnapshot(callback)
  }

  sendPasswordReset(email) {
    return this.auth.sendPasswordResetEmail(email);
  }

  errorMessageForAuthCode(code) {
    switch (code) {
      case 'auth/user-not-found': {
        return 'No account found with that email address.';
      }
      case 'auth/email-already-in-use': {
        return 'A user with that email already exists.';
      }
      case 'auth/wrong-password': {
        return 'Incorrect password. Please try again or reset your password.';
      }
      // Firebase SDK v9+ consolidates user-not-found + wrong-password into this
      case 'auth/invalid-credential': {
        return 'Incorrect email or password. Please try again or reset your password.';
      }
      case 'auth/invalid-email': {
        return 'Please enter a valid email address.';
      }
      case 'auth/too-many-requests': {
        return 'Too many failed attempts. Please wait a moment or reset your password.';
      }
      default: {
        return 'Error. Please try again.';
      }
    }
  }

  createEmployer(input) {
    // check if this org already exists
    return this.findCompany(input.companyName)
      .then(snapshot => {
        if (snapshot.empty) {
          // new org with user as admin, create it
          return this.firestore.collection("organizations").add({
            name: input.companyName,
            owner: this.auth.currentUser.uid,
            created: Date()
          }).then(result => {
            let profile = {
              type: 'employer',
              id: this.auth.currentUser.uid,
              email: input.email,
              created: Date(),
              isOwner: true,
              organization: result.id,
              companyName: input.companyName,
              resumeFilePaths: [],
              tagData: { categories: [] },
              approvalStatus: 'onboarding',
            };
            return this.firestore.collection("users").doc(this.auth.currentUser.uid).set(profile)
              .then(() => {
                return profile;
              });
          });
        } else {
          // should never have more than one org
          let org = snapshot.docs[0];
          let profile = {
            type: 'employer',
            id: this.auth.currentUser.uid,
            email: input.email,
            created: Date(),
            isOwner: false,
            organization: org.id,
            companyName: input.companyName,
            resumeFilePaths: [],
            tagData: { categories: [] },
            approvalStatus: 'onboarding',
          }
          return this.firestore.collection("users").doc(this.auth.currentUser.uid).set(profile)
            .then(() => {
              return profile;
            });
        }
      })
  }

  createAthlete(input) {
    const profile = {
      type: 'athlete',
      id: this.auth.currentUser.uid,
      email: input.email,
      created: Date(),
      resumeFilePaths: [],
      tagData: { categories: [] },
      approvalStatus: 'onboarding',
      approvalSubmittedAt: null,
    }

    return this.firestore.collection("users").doc(this.auth.currentUser.uid).set(profile).then(() => {
      return profile;
    });
  }

  /**
   * Creates a profile for org/professional types that are not athletes and
   * not employers (coach, trainer, agency, collective, team, scout,
   * individual_agent, medical_services, etc.).
   * These types are auto-approved — they don't go through the athlete gate.
   */
  createOrgProfile(input) {
    const profile = {
      type: input.type,
      id: this.auth.currentUser.uid,
      email: input.email,
      created: Date(),
      resumeFilePaths: [],
      tagData: { categories: [] },
      approvalStatus: 'onboarding',
    };
    return this.firestore.collection('users').doc(this.auth.currentUser.uid).set(profile).then(() => profile);
  }

  signUp(input) {
    return this.auth.createUserWithEmailAndPassword(input.email, input.password)
      .then(() => {

        this.trackAnalyticsEvent('sign_up', { userType: input.type });
        this.amplitudeClient.setUserId(this.auth.currentUser.uid);

        const EMPLOYER_TYPES = ['employer'];
        const ATHLETE_TYPES  = ['athlete'];

        if (EMPLOYER_TYPES.includes(input.type)) {
          return this.createEmployer(input);
        } else if (ATHLETE_TYPES.includes(input.type)) {
          return this.createAthlete(input);
        } else {
          // coach, trainer, agency, collective, team, scout,
          // individual_agent, medical_services, etc.
          return this.createOrgProfile(input);
        }

      })
      // success is handled by firebase auth, just handle error
      .catch(error => {
        console.log(error);
        // for now, just assuming this failed at the auth step, rethrow error for the App to handle

        throw error;
      });
  }

  getProfile() {
    return this.firestore.collection("users").doc(this.auth.currentUser.uid).get();
  }

  updateProfile(profileUpdates) {
    return this.firestore.collection("users").doc(this.auth.currentUser.uid).update(profileUpdates)
  }

  downloadFiles(files, prefixString) {
    console.log(files);
    return Promise.all(files.map(file => {
      return this.storage.ref(file).getDownloadURL().then((url) => {
        return new Promise(resolve => {
          console.log(url);
          let xhr = new XMLHttpRequest();
          xhr.responseType = 'blob';
          xhr.onload = function () {
            resolve(xhr.response);
          };
          xhr.open('GET', url);
          xhr.send();
        });

      }).catch(function (error) {
        console.log(error);
      });
    })
    ).then((r) => {
      return r.map((blob, index) => {
        let file = new File([blob], (prefixString !== undefined) ? [prefixString, index + 1].join('-') : uuidv4(), { type: blob.type });
        Object.assign(file, {
          preview: URL.createObjectURL(file)
        });
        return file;
      });
    });
  }


  getOrganization(orgId) {
    return this.firestore.collection("organizations").doc(orgId).get();
  }

  updateJob(job) {

    const document = this.firestore.collection("jobs").doc(job.id);

    return document.set(job, { merge: true }).then(result => {
      console.log(result);
    })
  }

  postJob(job) {

    const document = this.firestore.collection("jobs").doc();

    return document.set({
      ...job,
      created: Date(),
      id: document.id
    }).then(result => {
      console.log(result);
    })
  }

  deleteJob(job) {
    const document = this.firestore.collection("jobs").doc(job.id);
    return document.delete()
  }

  // ─── Opportunities (canonical collection — replaces jobs for new posts) ───

  async postOpportunity(data) {
    const ref = this.firestore.collection('opportunities').doc();
    await ref.set({
      ...data,
      id: ref.id,
      createdBy: this.auth.currentUser.uid,
      status: 'active',
      created: app.firestore.FieldValue.serverTimestamp(),
      updatedAt: new Date(),
    });
    return ref.id;
  }

  async updateOpportunityWeb(oppId, data) {
    return this.firestore.collection('opportunities').doc(oppId)
      .set({ ...data, updatedAt: new Date() }, { merge: true });
  }

  async getMyOpportunities() {
    const uid = this.auth.currentUser.uid;
    const snap = await this.firestore.collection('opportunities')
      .where('createdBy', '==', uid)
      .where('status', '==', 'active')
      .get();
    return snap.docs.map(d => ({ id: d.id, ...d.data() }));
  }

  async getMyActiveOpportunityCount() {
    const opps = await this.getMyOpportunities();
    return opps.length;
  }

  uploadFiles(files) {

    let ref = this.storage.ref().child('resumes').child(this.auth.currentUser.uid);

    return Promise.all(
      files.map((file) => {
        let extension = file.name.split('.').pop();

        return ref.child([uuidv4(), extension].join('.')).put(file);
      })
    ).then((result) => {
      console.log('upload complete', result);
      return result;
    });
  }

  getAthletes(startingAfter) {
    if (!startingAfter) {
      return this.firestore.collection('users').where('type', '==', 'athlete')
        .limit(100)
        .get();
    } else {
      return this.firestore.collection('users').where('type', '==', 'athlete')
        .limit(100)
        .startAfter(startingAfter)
        .get();
    }
  }

  /**
   * Jobs, newest-promoted first.
   *
   * `isActive` is filtered server-side. Nothing filtered on it before, so
   * long-filled scraped ATS listings kept rendering — a spot check of the
   * top-100 window found 57 hard 404s and 10 expired redirects. 2,441 of the
   * 2,492 jobs are now flagged inactive; the flag is the only thing keeping
   * them out, so do not drop this clause.
   * Needs the jobs(isActive, promotionPriority desc) composite index.
   */
  getJobs(startingAfter, organizationId) {
    let q = this.firestore.collection('jobs').where('isActive', '==', true);
    if (organizationId && organizationId.length > 0) {
      q = q.where('organizationId', '==', organizationId);
    }
    q = q.orderBy('promotionPriority', 'desc').limit(100);
    if (startingAfter) q = q.startAfter(startingAfter);
    return q.get();
  }


  getDashboardContent(type, startingAfter, organizationId) {
    console.log('Getting content starting after: ', startingAfter);
    if (type === 'employer') {
      return this.getAthletes(startingAfter);
    } else if (type === 'athlete') {
      return this.getJobs(startingAfter);
    } else if (type === 'employerJobPosts') {
      return this.getJobs(startingAfter, organizationId);
    } else {
      return Promise.resolve([]);
    }
  }

  getEventsContent(startingAfter) {
    if (!startingAfter) {
      return this.firestore.collection('events')
        .where('isActive', '==', true)
        .orderBy('priority', 'desc')
        .limit(20)
        .get();
    } else {
      return this.firestore.collection('events')
        .where('isActive', '==', true)
        .orderBy('priority', 'desc')
        .limit(20)
        .startAfter(startingAfter)
        .get();
    }
  }

  getMediaContent(startingAfter) {
    if (!startingAfter) {
      return this.firestore.collection('media')
        .orderBy('created', 'desc')
        .limit(20)
        .get();
    } else {
      return this.firestore.collection('media')
        .orderBy('created', 'desc')
        .limit(20)
        .startAfter(startingAfter)
        .get();
    }
  }

  // ─── Calendar Events ──────────────────────────────────────────────────────
  // Schema: userEvents/{uid}/items/{eventId}
  // { title, type, location, description, startDate, startTime, isPublic, createdAt }

  /**
   * Fetch all calendar events for a user (one-time read, ordered by startDate).
   * @param {string} uid
   * @returns {Promise<object[]>}
   */
  getUserCalendarEvents(uid) {
    return this.db
      .collection('userEvents')
      .doc(uid)
      .collection('items')
      .orderBy('startDate', 'asc')
      .get()
      .then(snap => snap.docs.map(d => ({ id: d.id, ...d.data() })));
  }

  /**
   * Subscribe to calendar events in real time.
   * @param {string} uid
   * @param {(events: object[]) => void} onUpdate
   * @returns {() => void} unsubscribe function
   */
  subscribeToCalendarEvents(uid, onUpdate) {
    return this.db
      .collection('userEvents')
      .doc(uid)
      .collection('items')
      .orderBy('startDate', 'asc')
      .onSnapshot(
        snap => onUpdate(snap.docs.map(d => ({ id: d.id, ...d.data() }))),
        err => { console.error('[Firebase] subscribeToCalendarEvents error:', err); onUpdate([]); }
      );
  }

  /**
   * Add a new calendar event.
   * @param {object} eventData
   * @returns {Promise<string>} event ID
   */
  addCalendarEvent(eventData) {
    const uid = this.auth.currentUser?.uid;
    if (!uid) return Promise.reject(new Error('Not authenticated'));
    return this.db
      .collection('userEvents')
      .doc(uid)
      .collection('items')
      .add({ ...eventData, createdAt: new Date() })
      .then(ref => ref.id);
  }

  /**
   * Delete a calendar event.
   * @param {string} eventId
   * @returns {Promise<void>}
   */
  deleteCalendarEvent(eventId) {
    const uid = this.auth.currentUser?.uid;
    if (!uid) return Promise.reject(new Error('Not authenticated'));
    return this.db
      .collection('userEvents')
      .doc(uid)
      .collection('items')
      .doc(eventId)
      .delete();
  }

  /**
   * Update a calendar event.
   * @param {string} eventId
   * @param {object} updates
   * @returns {Promise<void>}
   */
  updateCalendarEvent(eventId, updates) {
    const uid = this.auth.currentUser?.uid;
    if (!uid) return Promise.reject(new Error('Not authenticated'));
    return this.db
      .collection('userEvents')
      .doc(uid)
      .collection('items')
      .doc(eventId)
      .update({ ...updates, updatedAt: new Date() });
  }

  // ─── Commerce / Stripe Connect ────────────────────────────────────────────
  // These wrap Cloud Functions in functions/stripe.js via httpsCallable.

  _callable(name) {
    return this.functions.httpsCallable(name);
  }

  async createStripeConnectAccount(email, name, returnPath = '/commerce-hub') {
    const fn = this._callable('createStripeConnectAccount');
    const result = await fn({
      email,
      name,
      returnUrl:  window.location.origin + returnPath + '?stripe_connected=true',
      refreshUrl: window.location.origin + returnPath + '?stripe_refresh=true',
    });
    return result.data;
  }

  async checkStripeConnectStatus() {
    const fn = this._callable('checkStripeConnectStatus');
    const result = await fn({});
    return result.data;
  }

  // ─── Marketplace Listings ─────────────────────────────────────────────────

  async uploadProductImage(file) {
    const uid = this.auth.currentUser?.uid;
    if (!uid) throw new Error('Not authenticated');
    const ext = (file.name || 'img.jpg').split('.').pop();
    const path = `products/${uid}_${Date.now()}.${ext}`;
    const ref = this.storage.ref(path);
    await ref.put(file);
    return ref.getDownloadURL();
  }

  async postProduct(payload) {
    const fn = this._callable('createProductListing');
    const result = await fn(payload);
    return result.data; // { id }
  }

  async updateProductListing(productId, updates) {
    const fn = this._callable('updateProductListing');
    const result = await fn({ productId, updates });
    return result.data; // { ok: true }
  }

  async getProductById(productId) {
    const doc = await this.firestore.collection('products').doc(productId).get();
    return doc.exists ? { id: doc.id, ...doc.data() } : null;
  }

  async createProductCheckoutSessionWeb(productId, quantity = 1) {
    const fn = this._callable('createProductCheckoutSession');
    const result = await fn({
      productId,
      quantity,
      successUrl: window.location.origin + '/marketplace?checkout=success',
      cancelUrl: window.location.origin + '/marketplace?checkout=cancelled',
    });
    return result.data; // { url, sessionId }
  }

  async createStripeConnectLoginLink() {
    const fn = this._callable('createStripeConnectLoginLink');
    const result = await fn({});
    return result.data;
  }

  async syncStripeProducts() {
    const fn = this._callable('syncStripeProducts');
    const result = await fn({});
    return result.data;
  }

  // ─── Affiliate Requests ────────────────────────────────────────────────────

  /**
   * Called by AffiliateRequestModal when an athlete applies to promote a product.
   * Writes a document to the global `affiliateRequests` collection.
   * status will be 'pending' or 'approved' (for auto-approve products).
   */
  async submitAffiliateRequest(data) {
    const uid = this.auth.currentUser?.uid;
    if (!uid) throw new Error('Not authenticated');
    const doc = this.db.collection('affiliateRequests').doc();
    await doc.set({
      ...data,
      id: doc.id,
      createdAt: new Date().toISOString(),
    });
    return { id: doc.id };
  }

  /**
   * Fetch all affiliate requests where the authenticated user is the brand/company owner.
   * Used by CompanyProductDashboard → Affiliates tab.
   */
  async getAffiliateRequests() {
    const uid = this.auth.currentUser?.uid;
    if (!uid) return [];
    const snap = await this.db.collection('affiliateRequests')
      .where('companyCreator', '==', uid)
      .orderBy('createdAt', 'desc')
      .get();
    return snap.docs.map(d => ({ id: d.id, ...d.data() }));
  }

  /**
   * Approve or reject an affiliate request.
   * @param {string} reqId  Firestore document ID
   * @param {'approved'|'rejected'} status
   */
  async updateAffiliateRequestStatus(reqId, status) {
    await this.db.collection('affiliateRequests').doc(reqId).update({
      status,
      reviewedAt: new Date().toISOString(),
    });
  }

  async connectShopifyStore(storeUrl, accessToken) {
    // Store Shopify credentials in Firestore (no cloud function needed)
    const uid = this.auth.currentUser?.uid;
    if (!uid) throw new Error('Not authenticated');
    // Verify the store by fetching shop info via a proxy-safe approach
    // Store credentials encrypted in Firestore
    await this.db.collection('users').doc(uid).update({
      'shopify.storeUrl': storeUrl,
      'shopify.connected': true,
      'shopify.connectedAt': new Date(),
    });
    // Use storeName derived from URL
    const storeName = storeUrl.replace('https://', '').replace('http://', '').replace('.myshopify.com', '').split('.')[0];
    // Store token securely in a subcollection
    await this.db.collection('users').doc(uid).collection('privateData').doc('shopify').set({
      storeUrl,
      accessToken,
      storeName,
      connectedAt: new Date(),
    });
    return { storeName, storeDomain: storeUrl };
  }

  async checkShopifyStoreStatus() {
    const uid = this.auth.currentUser?.uid;
    if (!uid) return { connected: false };
    const doc = await this.db.collection('users').doc(uid).collection('privateData').doc('shopify').get();
    if (!doc.exists) return { connected: false };
    const data = doc.data();
    return { connected: true, storeName: data.storeName, storeDomain: data.storeUrl };
  }

  async syncShopifyProducts() {
    // Client-side sync: fetch from Shopify Storefront API and write to Firestore
    const uid = this.auth.currentUser?.uid;
    if (!uid) throw new Error('Not authenticated');
    const shopDoc = await this.db.collection('users').doc(uid).collection('privateData').doc('shopify').get();
    if (!shopDoc.exists) throw new Error('Shopify not connected');
    const { storeUrl, accessToken, storeName } = shopDoc.data();
    // Normalize URL
    const domain = storeUrl.includes('myshopify.com') ? storeUrl : `${storeUrl}.myshopify.com`;
    const endpoint = `https://${domain}/api/2024-01/graphql.json`;
    const query = `{ products(first: 50) { edges { node { id title handle priceRange { minVariantPrice { amount currencyCode } } images(first: 1) { edges { node { url } } } status } } } }`;
    const resp = await fetch(endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Shopify-Storefront-Access-Token': accessToken },
      body: JSON.stringify({ query }),
    });
    if (!resp.ok) throw new Error(`Shopify API error: ${resp.status}`);
    const json = await resp.json();
    const products = (json.data?.products?.edges || []).map(({ node }) => ({
      id: node.id,
      name: node.title,
      price: parseFloat(node.priceRange?.minVariantPrice?.amount || 0),
      currency: node.priceRange?.minVariantPrice?.currencyCode || 'USD',
      status: node.status?.toLowerCase() === 'active' ? 'active' : 'inactive',
      imageUrls: node.images?.edges?.[0] ? [node.images.edges[0].node.url] : [],
      source: 'shopify_sync',
      storeName,
      syncedAt: new Date(),
    }));
    // Write to Firestore
    const batch = this.db.batch();
    const col = this.db.collection('users').doc(uid).collection('products');
    // Remove old shopify products first
    const existing = await col.where('source', '==', 'shopify_sync').get();
    existing.docs.forEach(d => batch.delete(d.ref));
    products.forEach(p => {
      const ref = col.doc();
      batch.set(ref, { ...p, id: ref.id });
    });
    await batch.commit();
    return { synced: products.length };
  }

  async disconnectShopifyStore(archiveProducts = false) {
    const uid = this.auth.currentUser?.uid;
    if (!uid) throw new Error('Not authenticated');
    await this.db.collection('users').doc(uid).collection('privateData').doc('shopify').delete();
    await this.db.collection('users').doc(uid).update({ 'shopify.connected': false });
    if (archiveProducts) {
      const products = await this.db.collection('users').doc(uid).collection('products').where('source', '==', 'shopify_sync').get();
      const batch = this.db.batch();
      products.docs.forEach(d => batch.delete(d.ref));
      await batch.commit();
    }
  }

  async getMyProducts(uid) {
    if (!uid) return [];
    const snap = await this.db.collection('users').doc(uid).collection('products').get();
    return snap.docs.map(d => ({ id: d.id, ...d.data() }));
  }

  /**
   * Fetch all active products from the global `products` collection.
   * This is where syncStripeProducts (Cloud Function) writes synced products.
   * Used by ProductMarketplace to show real listings alongside mock data.
   */
  async getProducts() {
    const snap = await this.db.collection('products')
      .where('status', '==', 'active')
      .limit(200)
      .get();
    return snap.docs.map(d => ({ id: d.id, ...d.data() }));
  }

  /**
   * Fetch products from the global `products` collection for a specific seller.
   * Used by CompanyProductDashboard (Stripe Sync tab) to show what has been synced.
   */
  async getSellerProducts(uid) {
    if (!uid) return [];
    const snap = await this.db.collection('products')
      .where('creator', '==', uid)
      .get();
    return snap.docs.map(d => ({ id: d.id, ...d.data() }));
  }

  // ─── NIL Deals ────────────────────────────────────────────────────────────

  getNILDeals(startingAfter) {
    if (!startingAfter) {
      return this.firestore.collection('nilDeals')
        .orderBy('created', 'desc')
        .limit(20)
        .get();
    } else {
      return this.firestore.collection('nilDeals')
        .orderBy('created', 'desc')
        .limit(20)
        .startAfter(startingAfter)
        .get();
    }
  }

  postNILDeal(deal) {
    const doc = this.firestore.collection('nilDeals').doc();
    return doc.set({
      ...deal,
      id: doc.id,
      created: new Date(),
      authorUid: this.auth.currentUser?.uid || null,
    });
  }

  // ─── NIL Athletes ─────────────────────────────────────────────────────────

  searchAthletesForNIL(startingAfter) {
    const q = this.firestore.collection('users')
      .where('type', '==', 'athlete')
      .limit(24);
    if (!startingAfter) return q.get();
    return q.startAfter(startingAfter).get();
  }

  // ─── Camp Opportunities ───────────────────────────────────────────────────

  searchCampOpportunities() {
    return this.firestore.collection('campOpportunities')
      .orderBy('created', 'desc')
      .limit(50)
      .get()
      .then(snap => snap.docs.map(d => ({ id: d.id, ...d.data() })));
  }

  // ─── Fusion Feed ──────────────────────────────────────────────────────────

  /**
   * Paginated query for fusionPosts — the main social feed.
   * Returns up to `pageSize` posts ordered newest-first.
   */
  getFusionFeed(startingAfter, pageSize = 20) {
    const q = this.firestore.collection('fusionPosts')
      .orderBy('createdAt', 'desc')
      .limit(pageSize);
    if (!startingAfter) return q.get();
    return q.startAfter(startingAfter).get();
  }

  /**
   * Get the most recently created user accounts (for "X just joined" feed cards).
   */
  getRecentSignups(limit = 10) {
    return this.firestore.collection('users')
      .orderBy('created', 'desc')
      .limit(limit)
      .get()
      .then(snap => snap.docs.map(d => ({ uid: d.id, ...d.data() })));
  }

  /**
   * Get NIL collectives from Firestore.
   */
  getCollectives(startingAfter) {
    const q = this.firestore.collection('collectives')
      .orderBy('totalAthletes', 'desc')
      .limit(30);
    if (!startingAfter) return q.get();
    return q.startAfter(startingAfter).get();
  }

  /**
   * Get upcoming events (next 5) for feed injection.
   */
  getUpcomingEvents(limit = 5) {
    return this.firestore.collection('events')
      .where('isActive', '==', true)
      .orderBy('priority', 'desc')
      .limit(limit)
      .get()
      .then(snap => snap.docs.map(d => ({ id: d.id, ...d.data() })));
  }

  /**
   * Get active NIL deals for feed injection.
   */
  getActiveFeedDeals(limit = 5) {
    return this.firestore.collection('nilDeals')
      .orderBy('created', 'desc')
      .limit(limit)
      .get()
      .then(snap => snap.docs.map(d => ({ id: d.id, ...d.data() })));
  }

  // ─── Analytics ────────────────────────────────────────────────────────────

  trackAnalyticsEvent(event, params) {
    try { this.analytics.logEvent(event, params); } catch (_) {}
    try { this.amplitudeClient.logEvent(event, params); } catch (_) {}
  }

  /**
   * Identify a user across both Firebase Analytics and Amplitude.
   * Called from UserProvider on profile load and from AnalyticsService.identifyUser.
   */
  setUserId(userId) {
    try { this.analytics.setUserId(userId || null); } catch (_) {}
    try { this.amplitudeClient.setUserId(userId || null); } catch (_) {}
  }

  // ─── Profile Discovery ───────────────────────────────────────────────────

  /**
   * Fetch a user profile by their @handle.
   * Returns the document data + id, or null if not found.
   */
  async getUserByHandle(handle) {
    const snap = await this.db.collection('users')
      .where('handle', '==', handle)
      .limit(1)
      .get();
    if (snap.empty) return null;
    const doc = snap.docs[0];
    return { id: doc.id, ...doc.data() };
  }

  /**
   * Fetch all users of a given type ('team', 'collective', 'athlete', etc.)
   * that have isDiscoverable = true.
   */
  async getUsersByType(type) {
    const snap = await this.db.collection('users')
      .where('type', '==', type)
      .where('isDiscoverable', '==', true)
      .get();
    return snap.docs.map(d => ({ id: d.id, ...d.data() }));
  }

  // ─── Connections ─────────────────────────────────────────────────────────

  /**
   * Get connection requests for the current user.
   * @param {'incoming'|'outgoing'|'all'} direction
   */
  async getConnectionRequests(direction = 'all') {
    const uid = this.auth.currentUser?.uid;
    if (!uid) return [];
    let q = this.db.collection('connections').where('status', '==', 'pending');
    if (direction === 'incoming') q = q.where('receiverId', '==', uid);
    else if (direction === 'outgoing') q = q.where('requesterId', '==', uid);
    const snap = await q.get();
    return snap.docs.map(d => ({ id: d.id, ...d.data() }));
  }

  /**
   * Get accepted connections for the current user.
   */
  async getConnections() {
    const uid = this.auth.currentUser?.uid;
    if (!uid) return [];
    const [sent, received] = await Promise.all([
      this.db.collection('connections')
        .where('requesterId', '==', uid)
        .where('status', '==', 'accepted')
        .get(),
      this.db.collection('connections')
        .where('receiverId', '==', uid)
        .where('status', '==', 'accepted')
        .get(),
    ]);
    const all = [...sent.docs, ...received.docs];
    return all.map(d => ({ id: d.id, ...d.data() }));
  }

  /**
   * Send a connection request to another user.
   */
  async sendConnectionRequest(targetUid) {
    const uid = this.auth.currentUser?.uid;
    if (!uid) throw new Error('Not authenticated');
    return this.db.collection('connections').add({
      requesterId: uid,
      receiverId: targetUid,
      status: 'pending',
      createdAt: app.firestore.FieldValue.serverTimestamp(),
    });
  }

  /**
   * Update the status of a connection (e.g., accept/decline).
   */
  async updateConnectionStatus(connectionId, status) {
    return this.db.collection('connections').doc(connectionId).update({
      status,
      updatedAt: app.firestore.FieldValue.serverTimestamp(),
    });
  }

  // ─── Athlete Discovery ───────────────────────────────────────────────────

  /**
   * Fetch athletes belonging to a specific organization.
   */
  async getAthletesByOrganization(orgId) {
    const snap = await this.db.collection('users')
      .where('type', '==', 'athlete')
      .where('organizationId', '==', orgId)
      .get();
    return snap.docs.map(d => ({ id: d.id, ...d.data() }));
  }

  /**
   * Fetch athletes from a specific school.
   */
  async getAthletesBySchool(schoolName) {
    const snap = await this.db.collection('users')
      .where('type', '==', 'athlete')
      .where('school', '==', schoolName)
      .get();
    return snap.docs.map(d => ({ id: d.id, ...d.data() }));
  }

  /**
   * Fetch athletes on a specific team.
   */
  async getAthletesByTeam(teamName) {
    const snap = await this.db.collection('users')
      .where('type', '==', 'athlete')
      .where('team', '==', teamName)
      .get();
    return snap.docs.map(d => ({ id: d.id, ...d.data() }));
  }

  // ─── Athlete Brands ──────────────────────────────────────────────────────

  /**
   * Create a new athlete brand document.
   */
  async createAthleteBrand({ name, description, logoUrl, website, sport, category }) {
    const uid = this.auth.currentUser?.uid;
    if (!uid) throw new Error('Not authenticated');
    const doc = await this.db.collection('brands').add({
      name: name || '',
      description: description || '',
      logoUrl: logoUrl || '',
      website: website || '',
      sport: sport || '',
      category: category || '',
      ownerId: uid,
      members: [uid],
      createdAt: app.firestore.FieldValue.serverTimestamp(),
    });
    return doc.id;
  }

  /**
   * Upload a brand logo file to Firebase Storage and return the download URL.
   */
  async uploadBrandLogo(file) {
    const uid = this.auth.currentUser?.uid;
    if (!uid) throw new Error('Not authenticated');
    const ext = file.name?.split('.').pop() || 'jpg';
    const ref = this.storage.ref(`brands/${uid}/${uuidv4()}.${ext}`);
    await ref.put(file);
    return ref.getDownloadURL();
  }

  /**
   * Fetch all brands owned by the current user.
   */
  async getMyBrands() {
    const uid = this.auth.currentUser?.uid;
    if (!uid) return [];
    const snap = await this.db.collection('brands')
      .where('ownerId', '==', uid)
      .orderBy('createdAt', 'desc')
      .get();
    return snap.docs.map(d => ({ id: d.id, ...d.data() }));
  }

  /**
   * Fetch pending brand invites sent to the current user.
   */
  async getMyBrandInvites() {
    const uid = this.auth.currentUser?.uid;
    if (!uid) return [];
    const snap = await this.db.collection('brandInvites')
      .where('toUid', '==', uid)
      .where('status', '==', 'pending')
      .orderBy('createdAt', 'desc')
      .get();
    return snap.docs.map(d => ({ id: d.id, ...d.data() }));
  }

  /**
   * Accept a brand invite: update invite status and add user to brand members.
   */
  async acceptBrandInvite(inviteId) {
    const uid = this.auth.currentUser?.uid;
    if (!uid) throw new Error('Not authenticated');
    const inviteRef = this.db.collection('brandInvites').doc(inviteId);
    const invite = await inviteRef.get();
    if (!invite.exists) throw new Error('Invite not found');
    const { brandId } = invite.data();
    const batch = this.db.batch();
    batch.update(inviteRef, { status: 'accepted', updatedAt: app.firestore.FieldValue.serverTimestamp() });
    batch.update(this.db.collection('brands').doc(brandId), {
      members: app.firestore.FieldValue.arrayUnion(uid),
    });
    return batch.commit();
  }

  /**
   * Decline a brand invite: update invite status to 'declined'.
   */
  async declineBrandInvite(inviteId) {
    return this.db.collection('brandInvites').doc(inviteId).update({
      status: 'declined',
      updatedAt: app.firestore.FieldValue.serverTimestamp(),
    });
  }

  /**
   * Send a brand invite to another athlete.
   */
  async inviteToBrand(brandId, brandName, athleteUid, role = 'member') {
    const uid = this.auth.currentUser?.uid;
    if (!uid) throw new Error('Not authenticated');
    return this.db.collection('brandInvites').add({
      brandId,
      brandName,
      toUid: athleteUid,
      fromUid: uid,
      role,
      status: 'pending',
      createdAt: app.firestore.FieldValue.serverTimestamp(),
    });
  }
}

export default Firebase;
